import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HeadlineLarge, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { Permission } from '@shared/models/user.models';
import AccessRequired from '../components/AccessRequired';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { canUserUpdateCompanySettings } from "@shared/utils/AccessControl";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { reset, selectEditCompanyState } from "./editCompany.duck";
import { ConfigurationSection } from "../StyleComponents";
import { DomainBasedSignUpSetting } from '@shared/ui/organisms/settings/DomainBasedSignUpSetting';
import { LineManagementSettings } from '@shared/ui/organisms/settings/LineManagementSettings';
import { SevenDayWorkingWeekSettings } from '@shared/ui/organisms/settings/SevenDayWorkingWeekSettings';
import { TenantBasedSignUpSetting } from '@shared/ui/organisms/settings/TenantBasedSignUpSetting';
import { LockedSignupDomainsSetting } from '@shared/ui/organisms/settings/LockedSignupDomainsSetting';
import { WeeklyNotesSetting } from '@shared/ui/organisms/settings/WeeklyNotesSetting';
import { useTranslation } from "react-i18next";
import {
  DisableUpdatingPreviousWhereaboutsSetting
} from '@shared/ui/organisms/settings/DisableUpdatingPreviousWhereaboutsSetting';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { EditingFutureWhereaboutsSettings } from '@shared/ui/organisms/settings/EditingFutureWhereaboutsSettings';
import { CompanyNameSetting } from '@shared/ui/organisms/settings/CompanyNameSetting';
import { WhereaboutsIconPackSetting } from '@shared/ui/organisms/settings/WhereaboutsIconPackSetting';
import { AzureScimIntegrationSettingMessage } from '@shared/ui/organisms/settings/AzureScimIntegrationMessage';
import { BuildVersion } from '@shared/ui/organisms/settings/BuildVersion';
import { VisitorBookingSettings } from '@shared/ui/organisms/settings/VisitorBookingSettings';
import { PrivateGroupsSetting } from '@shared/ui/organisms/settings/PrivateGroupsSetting';
import { RestrictTeamManagement } from '@shared/ui/organisms/settings/RestrictTeamManagement';
import styled from "styled-components";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { GlobalEmailRemindersSetting } from '@shared/ui/organisms/settings/GlobalEmailRemindersSetting';
import { ShowBirthdaysOnWhereaboutsSetting } from '@shared/ui/organisms/settings/ShowBirthdaysOnWhereaboutsSetting';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { WhereaboutsReminderSetting } from "../components/WhereaboutsReminderSetting";
import { SchengenTrackingSetting } from '@shared/ui/organisms/settings/SchengenTrackingSetting';
import { McpIntegrationSetting } from '@shared/ui/organisms/settings/McpIntegrationSetting';

export default function CompanySettingsPage() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const state = useSelector(selectEditCompanyState);
  const editable = user?.role === Permission.CompanyAdmin;
  const isEditable = useMemo(() => canUserUpdateCompanySettings(user), [user]);
  const {t} = useTranslation();

  const company = user?.companyEntity;

  const openDeleteDialog = () => dispatch(openDialogWithPayload({
    activeDialog: DialogIdentifiers.DeleteCompanyDialog,
    payload: {company: {name: state.companyName}},
  }));

  useEffect(() => {
    dispatch(reset())
    return () => dispatch(reset());
  }, [dispatch]);

  if (!company) {
    return null // loading
  }

  return (
    <Container>

      {isEditable && <>
        <Section>
          <GoBackButton url={"/manage"} />
          <HeadlineLarge className="management__title">{t('company-settings-page.company-settings')}</HeadlineLarge>
          <HeadlineSmall style={{marginBottom: 16}}>{t('company-settings-page.company-preferences')}</HeadlineSmall>

          <AccessRequired visible={!editable} companyAdminsRequired={true}/>

          <ConfigurationSection>
            <CompanyNameSetting editable={editable} />
          </ConfigurationSection>

          <WhereaboutsIconPackSetting />
          <EditingFutureWhereaboutsSettings />
          <SevenDayWorkingWeekSettings />
          <LineManagementSettings />
          <WeeklyNotesSetting />
          <DisableUpdatingPreviousWhereaboutsSetting />
          <GlobalEmailRemindersSetting />
        </Section>

        <Section>
          <HeadlineSmall style={{marginBottom: 16}}>{t('company-settings-page.notifications')}</HeadlineSmall>
          <WhereaboutsReminderSetting/>
        </Section>

        <Section>
          <HeadlineSmall style={{marginBottom: 16}}>{t('company-settings-page.signup-and-register')}</HeadlineSmall>
          <DomainBasedSignUpSetting settingsView={true} />
          <TenantBasedSignUpSetting />
          <LockedSignupDomainsSetting />
        </Section>

        <Section>
          <HeadlineSmall style={{marginBottom: 16}}>{t('company-settings-page.azure-and-active-dir-settings')}</HeadlineSmall>
          <AzureScimIntegrationSettingMessage style={{}} />
        </Section>

        <Section>
          <HeadlineSmall style={{marginBottom: 16}}>{t('company-settings-page.other')}</HeadlineSmall>
          <VisitorBookingSettings />

          <PrivateGroupsSetting />
          <RestrictTeamManagement />
          <ShowBirthdaysOnWhereaboutsSetting />
          <SchengenTrackingSetting />
          <McpIntegrationSetting />
          <BuildVersion />
        </Section>

        <div className="management__row management__loneControl" style={{marginBottom: 32}}>
          <IconButton className="editCompanyPage__deleteCompanyBtn"
                      disabled={!editable}
                      icon={IconTypes.Bin}
                      text="button.delete-account"
                      color="darkGrey"
                      onClick={() => openDeleteDialog()}/>
        </div>
      </>}

    </Container>
  );
}

const Container = styled.div`
  margin: 16px auto 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 684px;
`

const Section = styled.div`
  width: 100%;
  background-color: white;
  max-width: 684px;
  padding: 0;
  margin-bottom: 64px;
  @media (${tablet}) {
    margin-bottom: 32px;
    border-radius: 24px;
    padding: 48px 64px;
  }
`
